import React, { useEffect, useState } from "react";
import SearchBar from "../components/SearchBar";
import RecentSearches from "../components/RecentSearches";
import MetricCards from "../components/MetricCards";
import TrendChart from "../components/TrendChart";
import Tabs from "../components/Tabs";
import OverviewGrid from "../components/OverviewGrid";
import FinancialTable from "../components/FinancialTable";
import AIAssistant from "../components/AIAssistant";
import Spinner from "../components/Spinner";
import TickerTape from "../components/TickerTape";
import { fetchAIInsights, fetchDashboard, fetchRecentSearches, fetchBalanceSheet, fetchCashFlow } from "../api";


const DashboardPage = () => {
  const [ticker, setTicker] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [overview, setOverview] = useState([]);
  const [income, setIncome] = useState([]);
  const [balance, setBalance] = useState([]);
  const [cash, setCash] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [cashLoading, setCashLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [recent, setRecent] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRecent();

    // Check for pending state (restoring after login redirect)
    const pendingJson = sessionStorage.getItem("pending_dashboard");
    if (pendingJson) {
      try {
        const pending = JSON.parse(pendingJson);
        if (pending.ticker) {
          loadDashboard(pending.ticker).then(() => {
            if (pending.tab) setActiveTab(pending.tab);
          });
        }
      } catch (e) {
        console.error("Failed to restore dashboard state", e);
      }
      sessionStorage.removeItem("pending_dashboard");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRecent = async () => {
    const items = await fetchRecentSearches();
    setRecent(items || []);
  };

  const loadDashboard = async (symbol) => {
    const target = (symbol || "").trim();
    if (!target) return;
    setLoading(true);
    const data = await fetchDashboard(target);
    setTicker(target.toUpperCase());
    setSnapshot(data.snapshot);
    setOverview(data.overview);
    setIncome(data.income);
    // reset lazily loaded tables and AI state for the new ticker
    setBalance([]);
    setCash([]);
    setBalanceLoading(false);
    setCashLoading(false);
    setAi(null);
    setActiveTab("overview");
    setLoading(false);
  };

  const handleAskAi = async (question, token) => {
    const response = await fetchAIInsights({ ticker, question }, token);
    setAi(response);
    return response;
  };

  const company = snapshot?.company;
  const hasDashboard = Boolean(snapshot);

  const ensureBalanceLoaded = async () => {
    if (!ticker || balanceLoading || balance.length) return;
    setBalanceLoading(true);
    const data = await fetchBalanceSheet(ticker);
    setBalance(data || []);
    setBalanceLoading(false);
  };

  const ensureCashLoaded = async () => {
    if (!ticker || cashLoading || cash.length) return;
    setCashLoading(true);
    const data = await fetchCashFlow(ticker);
    setCash(data || []);
    setCashLoading(false);
  };

  const handleTabChange = (id) => {
    setActiveTab(id);
    if (id === "balance") ensureBalanceLoaded();
    if (id === "cash") ensureCashLoaded();
  };

  const tabs = hasDashboard
    ? [
      { id: "overview", label: "Overview", content: <OverviewGrid overview={overview} /> },
      {
        id: "ai",
        label: "AI Analysis",
        content: <AIAssistant ticker={ticker} onAsk={handleAskAi} answer={ai} />,
      },
      { id: "income", label: "Income Statement", content: <FinancialTable title="Income Statement" rows={income} /> },
      {
        id: "balance",
        label: "Balance Sheet",
        content: balanceLoading ? <Spinner /> : (
          <FinancialTable
            title="Balance Sheet"
            rows={balance.length ? balance : []}
          />
        ),
      },
      {
        id: "cash",
        label: "Cash Flow",
        content: cashLoading ? <Spinner /> : (
          <FinancialTable
            title="Cash Flow"
            rows={cash.length ? cash : []}
          />
        ),
      },
    ]
    : [];

  return (
    <div className="stack">
      <h1 style={{ margin: 0 }}>Financial Insights</h1>
      <p className="tag">Search companies, view metrics, and ask AI questions.</p>

      <SearchBar onSelect={loadDashboard} defaultValue={ticker} />
      <RecentSearches items={recent} onSelect={loadDashboard} />

      {!hasDashboard && !loading && (
        <>
          <div className="panel">Search to load a company's dashboard or select a ticker below.</div>
          <TickerTape onTickerClick={loadDashboard} />
        </>
      )}

      {loading && <div className="panel"><Spinner /></div>}

      {!loading && hasDashboard && (
        <>
          <div className="panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div className="pill">
                  <span>{company.exchange}</span>
                  <span>/</span>
                  <span>{company.sector}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 8 }}>
                  <h2 style={{ margin: 0 }}>
                    {company.name} ({company.ticker})
                  </h2>
                  <span className={`badge ${company.price_change >= 0 ? "status-positive" : "status-negative"}`}>
                    {company.price_change >= 0 ? "+" : ""}
                    {company.price_change}%
                  </span>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="tag">Price</div>
                <div className="card-value">${company.price?.toFixed(2)}</div>
              </div>
            </div>
          </div>

          <MetricCards metrics={snapshot.metrics} />

          <div className="grid two">
            <TrendChart title="Revenue Trend (Quarterly)" data={snapshot.revenue_trend} color="#14b8ff" />
            <TrendChart title="EPS Trend (Quarterly)" data={snapshot.eps_trend} color="#3b68ff" />
          </div>

          <Tabs tabs={tabs} active={activeTab} onChange={handleTabChange} />
        </>
      )}
    </div>
  );
};

export default DashboardPage;
